/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { ReportService } from '../services/report.service';
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    DoCheck,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { LoggerService } from '../../shared/services/logger.service';
import { ReportDefinition, ReportLaunchOptions } from '../state/report.model';
import { Actions, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { isEqual } from 'lodash-es';
import { map, takeUntil } from 'rxjs/operators';
import { ReportState } from '../state/report.state';
import { MapConfigService } from '../../map/services/mapConfig.service';
import { RestService } from '../../shared/services/rest.service';
import { IsolatedReportService } from '../services/isolatedReport.service';
import { SetFilterOptions } from '../state/report.actions';
import { DashGisUtil } from '../../shared/dash-gis-util';

export function isolatedReportFactory(injector: Injector, irs: IsolatedReportService) {
    return (
        irs ||
        new IsolatedReportService(
            injector.get(LoggerService),
            injector.get(MapConfigService),
            injector.get(RestService)
        )
    );
}

@Directive()
export abstract class TypedReportDirective
    implements OnInit, OnChanges, OnDestroy, DoCheck, AfterContentChecked, AfterViewInit {
    @Output() clearReports = new EventEmitter<void>();
    @Output() recordCount = new EventEmitter<number>();

    @Output() reportTitle = new EventEmitter<string>();
    @Input() launchOptions: ReportLaunchOptions;
    @Input() availableReports: ReportDefinition[];

    @Input() showControls: boolean;
    @Input() uid: string;

    abstract reportData: any[];

    protected disposeSubject = new Subject<void>();
    protected dispose$: Observable<void>;
    protected recordCountInternal = -1;

    private lastClientRect: DOMRect;

    constructor(
        protected logger: LoggerService,
        protected reportService: ReportService,
        protected isolatedReport: IsolatedReportService,
        protected cdr: ChangeDetectorRef,
        protected actions$: Actions,
        protected store: Store,
        protected el: ElementRef
    ) {
        this.dispose$ = this.disposeSubject.asObservable();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.uid) {
            this.isolatedReport.id = changes.uid.currentValue;
        }
        const optsChange = changes.launchOptions;
        if (optsChange?.currentValue && !isEqual(optsChange.currentValue, optsChange.previousValue)) {
            const filters = this.launchOptions.filters;
            if (filters && filters.length > 0) {
                this.store.dispatch(
                    new SetFilterOptions({
                        id: this.id,
                        reportName: this.definition.name,
                        filterOptions: filters,
                    })
                );
            }
        }
    }

    ngOnInit() {
        this.isolatedReport.markChanged$.pipe(takeUntil(this.dispose$)).subscribe(() => this.cdr.detectChanges());
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    ngDoCheck(): void {
        const dims = this.el.nativeElement.getBoundingClientRect();
        if (!DashGisUtil.compareRect(dims, this.lastClientRect)) {
            this.isolatedReport.setContainerDimension([dims.width, dims.height]);
            this.lastClientRect = dims;
        }
    }

    ngAfterContentChecked() {
        if (this.definition) {
            this.reportTitle.emit(this.definition.displayName);
        }
    }

    ngAfterViewInit(): void {
        // necessary to satisfy interface
    }

    setRecordCount(count: number) {
        this.recordCountInternal = count;
        this.recordCount.emit(count);
    }

    get definition(): ReportDefinition {
        return this.launchOptions.definition;
    }

    get featureReportKeys$(): Observable<any[]> {
        return this.store.select(ReportState.featureReportKeys).pipe(map((fn) => fn(this.definition.layer)));
    }

    get id() {
        return this.isolatedReport.id;
    }
}
